import { Link } from "react-router";
import { useFormatter, useLocale, useTranslations } from "use-intl";
import { ChevronRight, Medal } from "lucide-react";
import { toIsoDate } from "@liftquest/shared/dates";
import { lt } from "@liftquest/shared/localized";
import { Page, PageHeader, Section } from "@/components/app/page-header";
import { PageLoading } from "@/components/app/states";
import { Card } from "@/components/ui/card";
import { useProgress } from "@/lib/queries";
import { cn } from "@/lib/utils";
import { LogPastWorkoutButton } from "./log-past-workout";

const FEELINGS = ["", "😫", "😕", "🙂", "😃", "🤩"];

export function ProgressPage() {
  const locale = useLocale();
  const t = useTranslations("progress");
  const tw = useTranslations("weekdays.short");
  const tc = useTranslations("common");
  const format = useFormatter();
  const progress = useProgress();
  if (!progress.data) return <PageLoading />;
  const { totals, stats, exercises } = progress.data;
  const history = progress.data.history.map((s) => ({
    ...s,
    startedAt: new Date(s.startedAt),
    finishedAt: s.finishedAt ? new Date(s.finishedAt) : null,
  }));

  // 12-week calendar, Monday-first columns
  const firstWeek = new Date(progress.data.calendarStart);
  const byDay = new Map<string, "strength" | "cardio">();
  for (const d of progress.data.days) {
    const key = toIsoDate(new Date(d.startedAt));
    if (byDay.get(key) !== "strength") byDay.set(key, d.kind);
  }
  const today = toIsoDate(new Date());

  return (
    <Page>
      <PageHeader title={t("title")} actions={<LogPastWorkoutButton />} />
      <div className="mt-5 grid grid-cols-2 gap-2">
        <Tile label={t("workouts")} value={format.number(totals.workouts)} />
        <Tile
          label={t("volume")}
          value={`${format.number(stats.volumeKg / 1000, { maximumFractionDigits: 1 })} t`}
        />
        <Tile
          label={t("time")}
          value={
            totals.minutes < 60
              ? `${Math.round(totals.minutes)} min`
              : `${format.number(totals.minutes / 60, { maximumFractionDigits: 1 })} h`
          }
        />
        <Tile label={t("prs")} value={format.number(stats.prs)} />
      </div>

      <Section title={t("calendar")}>
        <Card className="gap-0 p-3">
          <div className="grid grid-cols-[auto_repeat(12,1fr)] gap-1">
            {Array.from({ length: 7 }, (_, dayIdx) => (
              <div key={dayIdx} className="contents">
                <span className="pr-1 text-[10px] leading-4 text-muted-foreground">
                  {dayIdx % 2 === 0 ? tw(String(dayIdx) as "0") : ""}
                </span>
                {Array.from({ length: 12 }, (_, weekIdx) => {
                  const d = new Date(firstWeek);
                  d.setDate(d.getDate() + weekIdx * 7 + dayIdx);
                  const key = toIsoDate(d);
                  const kind = byDay.get(key);
                  return (
                    <span
                      key={weekIdx}
                      title={key}
                      className={cn(
                        "aspect-square rounded-[4px]",
                        kind === "strength" ? "bg-primary" : kind === "cardio" ? "bg-chart-3" : "bg-foreground/[0.07]",
                        key === today && "ring-2 ring-foreground/40",
                        key > today && "opacity-30",
                      )}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </Card>
      </Section>

      {exercises.length > 0 && (
        <Section title={t("exerciseProgress")}>
          <ul className="divide-y rounded-2xl border bg-card">
            {exercises.map((exercise) => (
              <li key={exercise.id}>
                <Link to={`/progress/exercise/${exercise.id}`} className="flex items-center justify-between px-4 py-3">
                  <span className="truncate font-medium">{lt(exercise.name, locale)}</span>
                  <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section title={t("history")}>
        {history.length === 0 ? (
          <p className="rounded-2xl border border-dashed p-6 text-center text-sm text-muted-foreground">
            {t("noHistory")}
          </p>
        ) : (
          <ul className="space-y-2">
            {history.map((s) => (
              <li key={s.id}>
                <Link to={`/progress/session/${s.id}`}>
                  <Card className="flex-row items-center gap-3 px-4 py-3">
                    <div className="flex size-11 shrink-0 flex-col items-center justify-center rounded-xl bg-muted text-center leading-none">
                      <span className="text-[10px] uppercase text-muted-foreground">
                        {format.dateTime(s.startedAt, { month: "short" })}
                      </span>
                      <span className="text-lg font-bold">{s.startedAt.getDate()}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold">
                        {lt(s.workoutName, locale)} {s.feeling ? FEELINGS[s.feeling] : ""}
                      </p>
                      <p className="text-xs text-muted-foreground tabular-nums">
                        {tc("sets", { count: s.sets })}
                        {s.volume > 0 && ` · ${format.number(s.volume, { maximumFractionDigits: 0 })} kg`}
                        {s.finishedAt &&
                          ` · ${Math.max(1, Math.round((s.finishedAt.getTime() - s.startedAt.getTime()) / 60000))} min`}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 text-xs">
                      <span className="font-semibold text-xp-foreground dark:text-xp">+{s.xp} XP</span>
                      {s.prs > 0 && (
                        <span className="flex items-center gap-0.5 text-muted-foreground">
                          <Medal className="size-3.5" /> {s.prs}
                        </span>
                      )}
                    </div>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </Page>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <Card className="gap-0 px-4 py-3">
      <p className="text-2xl font-bold tabular-nums">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </Card>
  );
}
